import {
    getUrgentAlerts,
    URGENT_ALERTS_CHANGED_EVENT,
    type UrgentAlert,
    type UrgentAlertKey,
} from '@/lib/api/core/urgent-alerts-api'
import { SocketContext } from '@/lib/providers/socket-provider'
import { useAuth } from '@/store/hooks'
import { useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { FaWhatsapp } from 'react-icons/fa'
import { MdAutoAwesome } from 'react-icons/md'
import { Link } from 'react-router-dom'

const ICONS: Record<UrgentAlertKey, ReactNode> = {
    whatsapp_disconnected: <FaWhatsapp className="text-xl" />,
    ai_credits_exhausted: <MdAutoAwesome className="text-xl" />,
}

/**
 * Barra de ações urgentes no topo, irmã da `SubscriptionAlert`: número de
 * WhatsApp desconectado e créditos de IA esgotados. Só para o dono da conta
 * (userType COMPANY), que é quem resolve.
 *
 * Busca ao abrir e quando o socket (re)conecta; depois, só quando o backend
 * avisa que algo mudou (`urgent-alerts-changed` na sala da organização). Sem
 * consulta periódica.
 */
export default function UrgentAlertsBar() {
    const { isCompany } = useAuth()
    const { socket, isConnected } = useContext(SocketContext)
    const [alerts, setAlerts] = useState<UrgentAlert[]>([])

    const load = useCallback(async () => {
        const response = await getUrgentAlerts()
        // Falha de rede não apaga a barra que já estava na tela.
        if (response.success && response.result) setAlerts(response.result)
    }, [])

    useEffect(() => {
        if (!isCompany) {
            setAlerts([])
            return
        }
        void load()
    }, [isCompany, load])

    useEffect(() => {
        if (!isCompany || !socket || !isConnected) return
        // Ao reconectar, o que mudou durante a queda não chegou por evento.
        void load()
        const handleChanged = () => void load()
        socket.on(URGENT_ALERTS_CHANGED_EVENT, handleChanged)
        return () => {
            socket.off(URGENT_ALERTS_CHANGED_EVENT, handleChanged)
        }
    }, [isCompany, socket, isConnected, load])

    if (!isCompany || alerts.length === 0) return null

    return (
        <div role="alert">
            {alerts.map(alert => {
                const isError = alert.severity === 'error'
                return (
                    <div
                        key={alert.key}
                        className={`${
                            isError ? 'bg-error/10 border-error' : 'bg-warning/10 border-warning'
                        } border-b-2 px-4 py-3`}
                    >
                        <div className="container mx-auto">
                            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <div className={`shrink-0 ${isError ? 'text-error' : 'text-warning'}`}>
                                        {ICONS[alert.key]}
                                    </div>
                                    <p
                                        className={`text-sm font-medium ${
                                            isError ? 'text-error' : 'text-warning'
                                        }`}
                                    >
                                        {alert.message}
                                    </p>
                                </div>
                                <Link
                                    to={alert.href}
                                    className={`btn btn-sm ${isError ? 'btn-error' : 'btn-warning'} text-white shrink-0`}
                                >
                                    {alert.actionLabel}
                                </Link>
                            </div>
                        </div>
                    </div>
                )
            })}
        </div>
    )
}
